import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import { getMentorPerformance, type MentorPerfRow, type MentorReportStudent } from '../../lib/api';
import {
  attStatus, num, hours, plural, pctOf, fmtDay, fmtRange, daysAgo, daysBetween, todayIST, delta,
  previousPeriod, periodFromParams, periodParams, type Period, type ActiveFilter, type Delta,
  ReportHeader, HeaderButton, PeriodPicker, SelectFilter, SearchFilter, ToggleChip, FilterCard, Section, Card, KpiTile,
  HowCounted, AttendanceCell, Pill, Loading, ServerOutdated,
} from '../../components/ReportKit';

const ROLE_LABEL: Record<string, string> = { Standard: 'Mentor', Senior: 'Senior mentor', 'Student Coordinator': 'Student coordinator' };
const PHASE_LABEL: Record<string, string> = { 'First 5 Days': 'First 5 days', 'Mid Course': 'Mid course', 'Last 10 Days': 'Last 10 days' };

type Flag = 'atRisk' | 'idle' | 'lowAtt' | 'noClasses' | 'tasksPending';
const FLAGS: { key: Flag; label: string; title: string; test: (m: MentorPerfRow) => boolean }[] = [
  { key: 'atRisk', label: '⚠️ Has students at risk', title: 'An active student missed their last 3 classes in a row', test: m => m.atRiskCount > 0 },
  { key: 'idle', label: '💤 Has students with no class in 7+ days', title: "An active student hasn't attended for a week or more", test: m => m.idleCount > 0 },
  { key: 'lowAtt', label: '📉 Attendance under 60%', title: 'Attendance in their classes this period is under 60%', test: m => m.period.attPct != null && m.period.attPct < 60 },
  { key: 'noClasses', label: '🚫 No classes this period', title: 'Logged no classes in the period', test: m => m.period.held + m.period.noShows === 0 },
  { key: 'tasksPending', label: '📝 Tasks waiting to be checked', title: "Gave tasks in the period that aren't marked done or not done yet", test: m => m.period.tasksPending > 0 },
];

type SortKey = '' | 'hours' | 'attLow' | 'attHigh' | 'atRisk' | 'students' | 'name';
const SORTS: { key: SortKey; label: string; cmp: (a: MentorPerfRow, b: MentorPerfRow) => number }[] = [
  { key: '',         label: 'Most classes held',        cmp: (a, b) => b.period.held - a.period.held },
  { key: 'hours',    label: 'Most teaching hours',      cmp: (a, b) => b.period.minutes - a.period.minutes },
  { key: 'attLow',   label: 'Lowest attendance first',  cmp: (a, b) => (a.period.attPct ?? 101) - (b.period.attPct ?? 101) },
  { key: 'attHigh',  label: 'Highest attendance first', cmp: (a, b) => (b.period.attPct ?? -1) - (a.period.attPct ?? -1) },
  { key: 'atRisk',   label: 'Most students at risk',    cmp: (a, b) => b.atRiskCount - a.atRiskCount || b.idleCount - a.idleCount },
  { key: 'students', label: 'Most active students',     cmp: (a, b) => b.activeStudents - a.activeStudents },
  { key: 'name',     label: 'Name (A–Z)',               cmp: () => 0 },
];

const tasksDonePct = (t: { tasksDone: number; tasksMissed: number }) => pctOf(t.tasksDone, t.tasksDone + t.tasksMissed);

const DeltaText: React.FC<{ d: Delta | null }> = ({ d }) => d && (
  <span className="font-semibold" style={{ color: d.tone === 'good' ? '#15803d' : d.tone === 'bad' ? '#b91c1c' : '#6b7280' }}>{d.text}</span>
);

const Mini: React.FC<{ label: string; value: React.ReactNode; hint?: React.ReactNode }> = ({ label, value, hint }) => (
  <div className="bg-white rounded-xl border border-gray-100 px-3 py-2.5">
    <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{label}</p>
    <p className="text-sm font-bold text-gray-800 mt-0.5">{value}</p>
    {hint && <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">{hint}</p>}
  </div>
);

const LastAttended: React.FC<{ s: MentorReportStudent }> = ({ s }) => (
  <>
    <p className="text-xs text-gray-700">{s.lastAttended ? fmtDay(s.lastAttended) : 'Never'}</p>
    <p className="text-[11px] text-gray-400">
      {s.lastAttended ? daysAgo(s.daysIdle) : s.daysIdle != null ? `joined ${daysAgo(s.daysIdle)}` : ''}
    </p>
  </>
);

// ── One mentor, opened: their numbers in detail and their students ───────────

type StudentView = 'all' | 'atRisk' | 'idle' | 'lowAtt';

const MentorDetail: React.FC<{ m: MentorPerfRow; vs: string; today: string }> = ({ m, vs, today }) => {
  const [view, setView] = useState<StudentView>('all');
  const [withInactive, setWithInactive] = useState(false);
  const inactiveCount = m.students.filter(s => s.status !== 'Active').length;
  const pool = m.students.filter(s => withInactive || s.status === 'Active');
  const tests: Record<StudentView, (s: MentorReportStudent) => boolean> = {
    all: () => true,
    atRisk: s => s.atRisk,
    idle: s => s.idle,
    lowAtt: s => s.attPct != null && s.attPct < 60,
  };
  const list = pool.filter(tests[view]);
  const views: [StudentView, string][] = [['all', 'All'], ['atRisk', 'At risk'], ['idle', 'No class in 7+ days'], ['lowAtt', 'Attendance under 60%']];

  return (
    <div className="px-5 py-5 bg-gray-50/70 space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Mini label="Days taught" value={num(m.period.daysTaught)} hint="Days with at least one class logged" />
        <Mini label="Covered for others" value={num(m.period.covered)} hint="Classes held for another mentor's students" />
        <Mini label="Last class logged" value={m.lastClass ? fmtDay(m.lastClass) : 'Never'}
          hint={m.lastClass ? daysAgo(daysBetween(m.lastClass, today)) : undefined} />
        <Mini label="Tasks given in the period" value={`${num(m.period.tasksDone)} done`}
          hint={`${num(m.period.tasksMissed)} not done · ${num(m.period.tasksPending)} waiting to be checked`} />
      </div>

      {m.prev && vs && (
        <p className="text-xs text-gray-500 leading-relaxed">
          Compared with <b className="text-gray-700">{vs}</b>:{' '}
          classes held {num(m.prev.held)} → <b className="text-gray-700">{num(m.period.held)}</b> <DeltaText d={delta(m.period.held, m.prev.held, vs)} />
          {' · '}attendance {m.prev.attPct == null ? '—' : `${m.prev.attPct}%`} → <b className="text-gray-700">{m.period.attPct == null ? '—' : `${m.period.attPct}%`}</b>{' '}
          <DeltaText d={delta(m.period.attPct, m.prev.attPct, vs, { unit: ' pts' })} />
          {' · '}teaching {hours(m.prev.minutes)} → <b className="text-gray-700">{hours(m.period.minutes)}</b>
        </p>
      )}

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-100">
          <p className="text-xs font-bold text-gray-700 mr-1">Their students</p>
          {views.map(([key, label]) => (
            <ToggleChip key={key} on={view === key} onClick={() => setView(key)} count={pool.filter(tests[key]).length}>{label}</ToggleChip>
          ))}
          {inactiveCount > 0 && (
            <label className="ml-auto flex items-center gap-1.5 text-[11px] text-gray-500 cursor-pointer">
              <input type="checkbox" checked={withInactive} onChange={e => setWithInactive(e.target.checked)} />
              Include {plural(inactiveCount, 'student')} no longer active (had classes in the period)
            </label>
          )}
        </div>
        {list.length === 0 ? (
          <p className="text-center py-8 text-xs text-gray-400">{pool.length === 0 ? 'No students assigned' : 'No students in this view'}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-2">Student</th>
                  <th className="px-4 py-2">Course</th>
                  <th className="px-4 py-2 whitespace-nowrap">Classes this period</th>
                  <th className="px-4 py-2">Attendance</th>
                  <th className="px-4 py-2 whitespace-nowrap">Last attended</th>
                  <th className="px-4 py-2">Tasks</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {list.map(s => (
                  <tr key={s.studentId} className="border-t border-gray-50 align-top">
                    <td className="px-4 py-2.5">
                      <p className="font-semibold text-gray-800">{s.name}</p>
                      <p className="text-[11px] text-gray-400">{s.studentId}{s.phase && PHASE_LABEL[s.phase] ? ` · ${PHASE_LABEL[s.phase]}` : ''}</p>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600">{s.course || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-700 whitespace-nowrap">
                      {s.held + s.noShows === 0 ? <span className="text-gray-400">No classes</span> : <>{num(s.held)} of {num(s.held + s.noShows)} attended</>}
                    </td>
                    <td className="px-4 py-2.5"><AttendanceCell pct={s.attPct} /></td>
                    <td className="px-4 py-2.5 whitespace-nowrap"><LastAttended s={s} /></td>
                    <td className="px-4 py-2.5 text-xs whitespace-nowrap">
                      {s.tasksDone + s.tasksMissed + s.tasksPending === 0 ? <span className="text-gray-400">None given</span> : (
                        <>
                          <p className="font-semibold text-gray-700">{num(s.tasksDone)} done</p>
                          <p className="text-[11px] text-gray-400">{num(s.tasksMissed)} not done · {num(s.tasksPending)} waiting</p>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1 justify-end">
                        {s.atRisk && <Pill tone="critical">At risk</Pill>}
                        {s.idle && <Pill tone="warning">No class 7+ days</Pill>}
                        {s.status !== 'Active' && <Pill tone="neutral" icon={false}>{s.status}</Pill>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ── main page ─────────────────────────────────────────────────────────────────
// Filters: period and course (fetched), then role, active / inactive, name,
// "needs attention" flags and sort (on the page). All kept in the address.

const AdminMentorPerformance: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const [params, setParams] = useSearchParams();

  const [period, setPeriod] = useState<Period>(() => periodFromParams(params, 'month'));
  const [course, setCourse] = useState(() => params.get('course') || '');
  const [role, setRole]     = useState(() => params.get('role') || '');
  const [show, setShow]     = useState(() => params.get('show') || '');      // '' active, 'inactive', 'all'
  const [search, setSearch] = useState(() => params.get('q') || '');
  const [sort, setSort]     = useState<SortKey>(() => (params.get('sort') || '') as SortKey);
  const [flags, setFlags]   = useState<Flag[]>(() => (params.get('flags') || '').split(',').filter((f): f is Flag => FLAGS.some(x => x.key === f)));
  const [expanded, setExpanded] = useState<string | null>(() => params.get('mentor'));
  const [follow, setFollow] = useState<'atRisk' | 'idle'>(() => (params.get('follow') === 'idle' ? 'idle' : 'atRisk'));

  const [report, setReport]     = useState<MentorPerfRow[] | null>(null);
  const [courses, setCourses]   = useState<string[]>([]);
  const [loading, setLoading]   = useState(true);
  const [outdated, setOutdated] = useState(false);
  const latest = useRef(0);
  const scrolled = useRef(false);
  const today = todayIST();

  const prev = useMemo(() => previousPeriod(period), [period]);
  const prevFrom = prev ? prev.from : '';
  const prevTo   = prev ? prev.to : '';
  const vs = prev ? fmtRange(prev.from, prev.to) : '';

  const load = useCallback(async () => {
    const call = ++latest.current;
    setLoading(true);
    try {
      const res = await getMentorPerformance(adminPassword,
        { from: period.from, to: period.to, ...(prevFrom ? { prevFrom, prevTo } : {}) }, { course });
      if (call !== latest.current) return;
      const fresh = !!res.range;
      setOutdated(!fresh);
      if (fresh) { setReport(res.report); setCourses(res.options.courses); }
    } catch (err: any) {
      if (call === latest.current) toast.error(err.message);
    } finally {
      if (call === latest.current) setLoading(false);
    }
  }, [adminPassword, period.from, period.to, prevFrom, prevTo, course]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const next = new URLSearchParams(periodParams(period));
    const put = (k: string, v: string) => { if (v) next.set(k, v); };
    put('course', course); put('role', role); put('show', show); put('q', search); put('sort', sort);
    put('flags', flags.join(',')); put('mentor', expanded || '');
    if (params.get('follow')) next.set('follow', follow);
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [period, course, role, show, search, sort, flags, expanded, follow, params, setParams]);

  const rows = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    const cmp = (SORTS.find(s => s.key === sort) || SORTS[0]).cmp;
    return report
      .filter(m => (show === 'all' ? true : show === 'inactive' ? m.status === 'Inactive' : m.status !== 'Inactive'))
      .filter(m => !role || m.role === role)
      .filter(m => !q || m.name.toLowerCase().includes(q))
      .filter(m => !course || m.activeStudents > 0 || m.period.held + m.period.noShows > 0)
      .filter(m => flags.every(f => FLAGS.find(x => x.key === f)!.test(m)))
      .sort((a, b) => cmp(a, b) || a.name.localeCompare(b.name));
  }, [report, search, sort, show, role, course, flags]);

  // Totals for the mentors shown
  const team = useMemo(() => {
    const t = { held: 0, noShows: 0, minutes: 0, prevHeld: 0, prevNoShows: 0, prevMinutes: 0, atRisk: 0, idle: 0 };
    rows.forEach(m => {
      t.held += m.period.held; t.noShows += m.period.noShows; t.minutes += m.period.minutes;
      t.atRisk += m.atRiskCount; t.idle += m.idleCount;
      if (m.prev) { t.prevHeld += m.prev.held; t.prevNoShows += m.prev.noShows; t.prevMinutes += m.prev.minutes; }
    });
    return t;
  }, [rows]);
  const hasPrev = !!vs && rows.some(m => m.prev);
  const teamAtt = pctOf(team.held, team.held + team.noShows);

  // Students to follow up, across the mentors shown
  const followUp = useMemo(() => {
    const list = rows.flatMap(m => m.students.filter(s => s.status === 'Active').map(s => ({ ...s, mentor: m.name })));
    return {
      atRisk: list.filter(s => s.atRisk).sort((a, b) => (b.daysIdle ?? 0) - (a.daysIdle ?? 0)),
      idle: list.filter(s => s.idle).sort((a, b) => (b.daysIdle ?? 0) - (a.daysIdle ?? 0)),
    };
  }, [rows]);

  // Opened from the dashboard: bring the mentor or the follow-up list into view, once
  useEffect(() => {
    if (!report || scrolled.current) return;
    scrolled.current = true;
    const target = params.get('follow') ? 'follow-up' : expanded ? `mentor-${expanded}` : '';
    if (target) setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  }, [report, expanded, params]);

  const toggleFlag = (f: Flag) => setFlags(fs => (fs.includes(f) ? fs.filter(x => x !== f) : [...fs, f]));

  const active: ActiveFilter[] = [
    ...(course ? [{ label: `Course: ${course}`, onRemove: () => setCourse('') }] : []),
    ...(role ? [{ label: `Role: ${ROLE_LABEL[role] || role}`, onRemove: () => setRole('') }] : []),
    ...(show ? [{ label: show === 'inactive' ? 'Inactive mentors' : 'Active and inactive', onRemove: () => setShow('') }] : []),
    ...(search ? [{ label: `Name: “${search}”`, onRemove: () => setSearch('') }] : []),
    ...flags.map(f => ({ label: FLAGS.find(x => x.key === f)!.label, onRemove: () => toggleFlag(f) })),
  ];
  const clearAll = () => { setCourse(''); setRole(''); setShow(''); setSearch(''); setFlags([]); };

  const followList = followUp[follow];

  return (
    <TrackerLayout>
      <div className="space-y-6">
        <ReportHeader icon="👨‍🏫" title="Mentor Performance"
          intro="How much each mentor is teaching and how their students are doing. Class numbers follow the period you pick; numbers marked Now are as of today. Open a mentor to see each of their students."
          actions={<HeaderButton onClick={load}>↻ Refresh</HeaderButton>} />

        <FilterCard busy={loading && !!report} active={active} onClearAll={clearAll}
          showing={<>Showing <b className="text-gray-700">{report ? plural(rows.length, 'mentor') : '…'}</b> · {fmtRange(period.from, period.to)}{vs && <> · compared with {vs}</>}</>}>
          <PeriodPicker label="Period" value={period} onChange={setPeriod} />
          <div className="flex flex-wrap items-end gap-3">
            <SelectFilter label="Course" value={course} onChange={setCourse} allLabel="All courses"
              options={(courses.length ? courses : course ? [course] : []).map(c => ({ value: c, label: c }))} />
            <SelectFilter label="Role" value={role} onChange={setRole} allLabel="All roles"
              options={Object.keys(ROLE_LABEL).map(r => ({ value: r, label: ROLE_LABEL[r] }))} />
            <SelectFilter label="Show" value={show} onChange={setShow} allLabel="Active mentors"
              options={[{ value: 'inactive', label: 'Inactive mentors' }, { value: 'all', label: 'Active and inactive' }]} />
            <SelectFilter label="Sort by" value={sort} onChange={v => setSort(v as SortKey)} allLabel={SORTS[0].label}
              options={SORTS.slice(1).map(s => ({ value: s.key, label: s.label }))} />
            <SearchFilter label="Mentor name" value={search} onChange={setSearch} placeholder="Search by name…" />
          </div>
          <div>
            <span className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">Only mentors who…</span>
            <div className="flex flex-wrap gap-1.5">
              {FLAGS.map(f => (
                <ToggleChip key={f.key} on={flags.includes(f.key)} onClick={() => toggleFlag(f.key)} title={f.title}
                  count={report ? report.filter(f.test).filter(m => show === 'all' || (show === 'inactive' ? m.status === 'Inactive' : m.status !== 'Inactive')).length : undefined}>
                  {f.label}
                </ToggleChip>
              ))}
            </div>
          </div>
        </FilterCard>

        {outdated && <ServerOutdated />}

        {!report ? (loading ? <Loading text="Loading mentor performance…" /> : null) : (
          <div className={`space-y-8 transition-opacity ${loading ? 'opacity-60' : ''}`}>

            <Section title="Team totals" caption={`${fmtRange(period.from, period.to)} · the ${plural(rows.length, 'mentor')} shown below${course ? ` · ${course}` : ''}`}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <KpiTile label="Classes held" scope="period" value={num(team.held)}
                  delta={hasPrev ? delta(team.held, team.prevHeld, vs) : null}
                  hint={`${num(team.noShows)} no-shows besides`} />
                <KpiTile label="Attendance rate" scope="period" value={teamAtt == null ? '—' : `${teamAtt}%`}
                  status={attStatus(teamAtt)}
                  delta={hasPrev ? delta(teamAtt, pctOf(team.prevHeld, team.prevHeld + team.prevNoShows), vs, { unit: ' pts' }) : null}
                  hint="Present ÷ (present + absent) in their classes" />
                <KpiTile label="Teaching hours" scope="period" value={hours(team.minutes)}
                  delta={hasPrev ? delta(team.minutes / 60, team.prevMinutes / 60, vs, { format: n => n.toFixed(1), unit: ' h' }) : null}
                  hint="From the start and end times of classes held" />
                <KpiTile label="Students at risk" scope="now" value={num(team.atRisk)}
                  status={team.atRisk > 0 ? { tone: 'critical', label: 'Follow up' } : { tone: 'good', label: 'None' }}
                  hint={`Plus ${plural(team.idle, 'student')} with no class in 7+ days`}
                  onClick={() => document.getElementById('follow-up')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} cta="See who" />
              </div>
            </Section>

            <Section title="Mentors" caption="Click a mentor to see their students">
              <Card flush>
                {rows.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-sm font-semibold text-gray-500">No mentors match these filters</p>
                    {active.length > 0 && <button onClick={clearAll} className="mt-2 text-xs font-bold underline text-gray-500">Clear filters</button>}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                          <th className="px-4 py-2.5">Mentor</th>
                          <th className="px-4 py-2.5 text-right whitespace-nowrap">Active students <span className="normal-case font-normal text-gray-400">(now)</span></th>
                          <th className="px-4 py-2.5 text-right whitespace-nowrap">Classes held</th>
                          <th className="px-4 py-2.5 text-right whitespace-nowrap">No-shows</th>
                          <th className="px-4 py-2.5">Attendance</th>
                          <th className="px-4 py-2.5 text-right whitespace-nowrap">Teaching hours</th>
                          <th className="px-4 py-2.5 whitespace-nowrap">Tasks done</th>
                          <th className="px-4 py-2.5 text-right whitespace-nowrap">At risk <span className="normal-case font-normal text-gray-400">(now)</span></th>
                          <th className="px-2 py-2.5" />
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(m => {
                          const open = expanded === m.mentorId;
                          const taskPct = tasksDonePct(m.period);
                          return (
                            <React.Fragment key={m.mentorId}>
                              <tr id={`mentor-${m.mentorId}`} onClick={() => setExpanded(open ? null : m.mentorId)} aria-expanded={open}
                                className={`border-t border-gray-100 cursor-pointer transition-colors ${open ? 'bg-purple-50/60' : 'hover:bg-gray-50'}`}>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0" style={{ background: '#2b2240' }}>
                                      {m.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                      <p className="font-semibold text-gray-800 whitespace-nowrap">{m.name}</p>
                                      <p className="text-[11px] text-gray-400 whitespace-nowrap">
                                        {ROLE_LABEL[m.role] || m.role}{m.status === 'Inactive' ? ' · inactive' : ''}
                                      </p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-gray-700">{num(m.activeStudents)}</td>
                                <td className="px-4 py-3 text-right">
                                  <p className="tabular-nums font-bold text-gray-800">{num(m.period.held)}</p>
                                  {m.prev && <p className="text-[10px]"><DeltaText d={delta(m.period.held, m.prev.held, vs)} /></p>}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-gray-700">{num(m.period.noShows)}</td>
                                <td className="px-4 py-3"><AttendanceCell pct={m.period.attPct} /></td>
                                <td className="px-4 py-3 text-right tabular-nums text-gray-700">{hours(m.period.minutes)}</td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <p className="text-xs font-bold text-gray-700">{taskPct == null ? '—' : `${taskPct}%`}</p>
                                  {m.period.tasksPending > 0 && <p className="text-[10px] text-amber-700">{num(m.period.tasksPending)} waiting</p>}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  {m.atRiskCount > 0
                                    ? <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">{m.atRiskCount}</span>
                                    : <span className="text-gray-300">0</span>}
                                </td>
                                <td className="px-2 py-3 text-gray-400 text-xs">{open ? '▲' : '▼'}</td>
                              </tr>
                              {open && (
                                <tr>
                                  <td colSpan={9} className="p-0 border-t border-purple-100">
                                    <MentorDetail m={m} vs={vs} today={today} />
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </Section>

            <Section id="follow-up" title="Students to follow up" caption="Active students of the mentors shown, as of today">
              <Card flush>
                <div className="flex flex-wrap gap-2 px-5 pt-4 pb-3">
                  <ToggleChip on={follow === 'atRisk'} onClick={() => setFollow('atRisk')} count={followUp.atRisk.length}>⚠️ At risk — missed last 3 classes</ToggleChip>
                  <ToggleChip on={follow === 'idle'} onClick={() => setFollow('idle')} count={followUp.idle.length}>💤 No class in 7+ days</ToggleChip>
                </div>
                {followList.length === 0 ? (
                  <p className="text-center pb-10 pt-4 text-sm text-gray-400">Nobody here right now</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                          <th className="px-4 py-2">Student</th>
                          <th className="px-4 py-2">Mentor</th>
                          <th className="px-4 py-2">Course</th>
                          <th className="px-4 py-2 whitespace-nowrap">Last attended</th>
                          <th className="px-4 py-2 whitespace-nowrap">Classes this period</th>
                        </tr>
                      </thead>
                      <tbody>
                        {followList.map(s => (
                          <tr key={s.studentId} className="border-t border-gray-50 align-top">
                            <td className="px-4 py-2.5">
                              <p className="font-semibold text-gray-800">{s.name}</p>
                              <p className="text-[11px] text-gray-400">{s.studentId}</p>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-gray-600">{s.mentor}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-600">{s.course || '—'}</td>
                            <td className="px-4 py-2.5 whitespace-nowrap"><LastAttended s={s} /></td>
                            <td className="px-4 py-2.5 text-xs text-gray-700 whitespace-nowrap">
                              {s.held + s.noShows === 0 ? <span className="text-gray-400">No classes</span> : <>{num(s.held)} of {num(s.held + s.noShows)} attended</>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </Section>

            <HowCounted items={[
              ['Classes held / No-shows', 'Classes the mentor took in the period with the student present / absent. A class covered by a substitute counts for the substitute. Mentor Leave and Institute Leave days are neither.'],
              ['Attendance', 'Present ÷ (present + absent) in the classes they took. 80% or more is Good, 60–79% Fair, under 60% Low.'],
              ['Teaching hours', 'Added up from the start and end times of the classes held — a class logged without times adds nothing.'],
              ['Tasks done', "Of the tasks given in their classes and checked, the share marked done. Waiting = given but not marked done or not done yet."],
              ['Active students (now)', 'Students assigned to the mentor whose status is Active today.'],
              ['At risk / No class in 7+ days', "An active student whose last 3 classes were all absences / who hasn't attended for 7 days or more (from joining, if never)."],
              ['Their students', "Today's active students of the mentor, with every class each had in the period — whoever took it. Students no longer active are listed if they had a class in the period."],
              ['▲ / ▼ changes', 'Compared with the same stretch just before — this month so far against the same days last month, and so on.'],
            ]} />
          </div>
        )}
      </div>
    </TrackerLayout>
  );
};

export default AdminMentorPerformance;
